/**
 *  @module Resources/Charges
 */

import { Connector } from "checkout/ts/Connector";
import { isTest } from "checkout/ts/utils/environment";
import { SupportedLocale } from "common/locale/labels";
import { isInline } from "common/utils/browser";
import {
    AuthParams,
    ChargeItem,
    Charges as NodeCharges,
    ChargeStatus,
    DefinedRoute,
    HTTPMethod,
    PaymentType,
    ResponseErrorCode,
    SendData,
    TransactionTokenItem,
} from "univapay-node";

import { redirectInIFrame, setThreeDsParameters } from "../../../../../common/utils/redirect";

export type ChargeExpiry = {
    id: string;
    chargeId: string;
    expirationDate: string;
    extensionCount: number;
    createdOn: string;
    updatedOn: string;
};

type IssuerToken = {
    issuerToken: string;
    callMethod: "http_post" | "http_get";
    payload: Record<string, string>;
    paymentType: PaymentType;
    contentType: string;
};

export class Charges extends NodeCharges {
    private _expiry?: DefinedRoute;
    expiry(storeId: string, chargeId: string, data?: SendData<void>, auth?: AuthParams): Promise<ChargeExpiry> {
        this._expiry =
            this._expiry ?? this.defineRoute(HTTPMethod.GET, "/stores/:storeId/charges/:chargeId/expiry/latest");
        return this._expiry(data, undefined, auth, { storeId, chargeId });
    }

    private _issuerToken?: DefinedRoute;
    issuerToken(storeId: string, chargeId: string, auth?: AuthParams): Promise<IssuerToken> {
        this._issuerToken =
            this._issuerToken ??
            this.defineRoute(
                HTTPMethod.GET,
                "/stores/:storeId/charges/:chargeId/three_ds/issuer_token",
                undefined,
                undefined,
                undefined,
                undefined,
                ["payload"]
            );
        return this._issuerToken(null, null, auth, { storeId, chargeId });
    }

    async pollThreeDs(
        storeId: string,
        chargeId: string,
        locale: SupportedLocale,
        connector: Connector,
        token?: TransactionTokenItem
    ) {
        const promise = this.poll(
            storeId,
            chargeId,
            null,
            null,
            null,
            null,
            (charge: ChargeItem) => ![ChargeStatus.PENDING, ChargeStatus.AWAITING].includes(charge?.status) || isTest()
        );

        const charge = await (async () => {
            if (isInline()) {
                // In case of the inline form the 3DS iFrame will be opened from the client.
                // As the issuer token is concidered private information it will be fetch on the 3DS iFrame
                connector.emitter.emit("checkout:three-ds-authorization-open-modal", {
                    storeId,
                    chargeId,
                    jwt: this.api.jwtRaw,
                    endpoint: this.api.endpoint,
                    locale,
                });

                return promise;
            } else {
                const issuerToken = await this.issuerToken(storeId, chargeId);

                return redirectInIFrame<ChargeItem>({
                    url: `${this.api.endpoint}/three_ds/execute`,
                    onRedirectOpened: (iFrame: HTMLIFrameElement) => setThreeDsParameters(iFrame, issuerToken),
                    promise,
                    locale,
                });
            }
        })();

        return isTest()
            ? {
                  ...charge,
                  status: token.email.includes("+3ds-failure") ? ChargeStatus.ERROR : ChargeStatus.SUCCESSFUL,
                  error: token.email.includes("+3ds-failure")
                      ? { code: 400, message: ResponseErrorCode.CardInvalid }
                      : undefined,
              }
            : charge;
    }
}
